import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FullLoader } from "@/components/cast/Common";

export default function PassPage() {
  const { code } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/pass/${code}`).then((r) => setP(r.data)).catch((e) => setErr(errMsg(e))); }, [code]);
  if (!p && !err) return <FullLoader />;
  const remember = () => localStorage.setItem("cast_return", `/pass/${code}`);
  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/pass/${code}/claim`);
      await refresh();
      confetti({ particleCount: 90, spread: 75, origin: { y: 0.6 }, disableForReducedMotion: true });
      toast.success("Cast Plus is yours for 30 days!");
      setTimeout(() => nav("/deep-dive"), 900);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const btn = "inline-flex justify-center rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5";
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF7F2] p-5" data-testid="pass-page">
      <motion.div initial={{ opacity: 0, y: 20, rotate: -1 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ type: "spring", damping: 16 }}
        className="relative w-full max-w-lg overflow-hidden rounded-[2rem] bg-[#1E293B] p-8 text-amber-50 shadow-2xl sm:p-10">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-amber-300/25 blur-3xl" />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-300 text-amber-950"><Gift className="h-7 w-7" /></span>
        {err ? <p className="relative mt-6 font-display text-3xl" data-testid="pass-error">{err}</p> : (
          <div className="relative">
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.22em] text-amber-300">A gift for you</p>
            <h1 className="mt-2 font-display text-4xl font-semibold sm:text-5xl" data-testid="pass-title">{p.from_name} sent you 30 days of Cast Plus</h1>
            {p.message && <p className="mt-5 rounded-2xl bg-white/10 p-4 font-scripture text-lg italic" data-testid="pass-message">“{p.message}”</p>}
            <p className="mt-5 text-sm text-amber-100/80">Read the whole Bible in Deep Dive, with colour highlights, narrated chapters, a Scripture-cited Study Guide and daily reading plans. Free, nothing to pay.</p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="pass-actions">
              {p.status !== "open" ? <p className="font-semibold text-amber-200" data-testid="pass-status">{p.status === "claimed" ? "This gift has already been claimed." : "This gift link has expired."}</p>
                : user ? <button data-testid="pass-claim" disabled={busy} onClick={claim} className={`${btn} bg-amber-300 text-amber-950`}>{busy ? "Claiming…" : "Claim my 30 days"}</button>
                : <>
                  <Link to="/register" onClick={remember} data-testid="pass-register" className={`${btn} bg-amber-300 text-amber-950`}>Create my Cast account</Link>
                  <Link to="/login" onClick={remember} data-testid="pass-login" className={`${btn} border border-amber-200/40 text-amber-50`}>I have an account</Link>
                </>}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
